<template>
  <div>
    <b-row class="match-height">
      <b-col cols="12" md="4">
        <b-card>
          <small class="text-muted">На одобрении</small>
          <h3 class="mb-0 mt-50">{{ summary.pending_count || 0 }}</h3>
        </b-card>
      </b-col>
      <b-col cols="12" md="4">
        <b-card>
          <small class="text-muted">К выплате</small>
          <h3 class="mb-0 mt-50">{{ formatRub(summary.unpaid_amount || 0) }}</h3>
        </b-card>
      </b-col>
      <b-col cols="12" md="4">
        <b-card>
          <small class="text-muted">Выплачено</small>
          <h3 class="mb-0 mt-50">{{ formatRub(summary.paid_amount || 0) }}</h3>
        </b-card>
      </b-col>
    </b-row>

    <b-card no-body>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="3" class="mb-1">
            <label>Статус</label>
            <b-form-select v-model="statusFilter" :options="statusOptions" />
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-overlay :show="isLoading" rounded="sm">
      <b-card no-body>
        <b-card-body>
          <vue-good-table
            :rows="rows"
            :columns="columns"
            :sort-options="{ enabled: false }"
            :pagination-options="{ enabled: false }"
          >
            <template slot="emptystate">
              <div class="text-center text-muted py-2">Заявок нет</div>
            </template>
            <template slot="table-row" slot-scope="props">
              <div v-if="props.column.field === 'submission'">
                <submission-preview :submission="props.row" />
              </div>
              <span v-else-if="props.column.field === 'artist'">
                <b-link
                  v-if="props.row.artist"
                  :to="{ name: 'artist-view', params: { id: props.row.artist.id } }"
                >
                  {{ props.row.artist.title }}
                </b-link>
                <span v-else>—</span>
              </span>
              <div v-else-if="props.column.field === 'status'">
                <b-badge :variant="statusVariants[props.row.status] || 'light-secondary'">
                  {{ props.row.status_label }}
                </b-badge>
                <b-badge
                  v-if="props.row.status === 'approved' && props.row.is_paid"
                  variant="success"
                  class="ml-50"
                >
                  Оплачено
                </b-badge>
                <b-badge
                  v-else-if="props.row.status === 'approved' && !props.row.is_paid && props.row.amount > 0"
                  variant="info"
                  class="ml-50"
                >
                  К выплате
                </b-badge>
              </div>
              <span v-else-if="props.column.field === 'review_comment'">
                <em v-if="props.row.review_comment">{{ props.row.review_comment }}</em>
                <span v-else>—</span>
              </span>
              <span v-else-if="props.column.field === 'amount'">
                {{ props.row.amount > 0 ? formatRub(props.row.amount) : '—' }}
              </span>
              <span v-else-if="props.column.field === 'created_at'">
                {{ formatDateTime(props.row.created_at) }}
              </span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>

          <div v-if="total > perPage" class="d-flex justify-content-center mt-2">
            <b-pagination
              v-model="currentPage"
              :total-rows="total"
              :per-page="perPage"
              class="mb-0"
            />
          </div>
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BOverlay, BLink, BBadge, BFormSelect, BPagination,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { ref, watch } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatDateTime } from '@core/utils/filter'
import { formatRub } from '@/views/finance/utils'
import { SUBMISSION_STATUS_VARIANTS, SUBMISSION_STATUS_OPTIONS } from '@/libs/moderation'
import SubmissionPreview from './SubmissionPreview.vue'

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BOverlay,
    BLink,
    BBadge,
    BFormSelect,
    BPagination,
    VueGoodTable,
    SubmissionPreview,
  },
  setup() {
    const toast = useToast()
    const showError = title => toast({
      component: ToastificationContent,
      props: { title, icon: 'AlertTriangleIcon', variant: 'danger' },
    })

    const isLoading = ref(false)
    const rows = ref([])
    const total = ref(0)
    const perPage = ref(15)
    const currentPage = ref(1)
    const summary = ref({ pending_count: 0, unpaid_amount: 0, paid_amount: 0 })

    const statusFilter = ref('')
    const statusOptions = [{ value: '', text: 'Все' }, ...SUBMISSION_STATUS_OPTIONS]
    const statusVariants = SUBMISSION_STATUS_VARIANTS

    const columns = [
      { label: 'Заявка', field: 'submission' },
      { label: 'Артист', field: 'artist' },
      { label: 'Статус', field: 'status' },
      { label: 'Комментарий админа', field: 'review_comment' },
      { label: 'Сумма', field: 'amount' },
      { label: 'Дата', field: 'created_at' },
    ]

    const errorMessage = err => {
      const data = (err.response && err.response.data) || {}
      if (data.errors) return Object.values(data.errors)[0][0]
      return data.message || 'Ошибка'
    }

    const fetchSubmissions = () => {
      isLoading.value = true
      const params = { page: currentPage.value }
      if (statusFilter.value) params.status = statusFilter.value
      store.dispatch('moderation/fetchMy', params)
        .then(response => {
          const body = response.data || {}
          rows.value = body.data || []
          const meta = body.meta || {}
          total.value = meta.total || 0
          perPage.value = meta.per_page || 15
          if (body.summary) summary.value = body.summary
        })
        .catch(err => showError(errorMessage(err)))
        .finally(() => { isLoading.value = false })
    }

    // Смена фильтра — возврат на первую страницу; если страница уже первая, грузим сами
    watch(statusFilter, () => {
      if (currentPage.value === 1) fetchSubmissions()
      else currentPage.value = 1
    })
    watch(currentPage, fetchSubmissions)

    fetchSubmissions()

    return {
      isLoading,
      rows,
      total,
      perPage,
      currentPage,
      summary,
      statusFilter,
      statusOptions,
      statusVariants,
      columns,
      formatDateTime,
      formatRub,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
